import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import {
  CfcReportBackBar,
  CfcReportBackButton,
  CfcReportCloseButton,
  CfcReportDialog,
  CfcReportError,
  CfcReportFrameArea,
  CfcReportHeader,
  CfcReportPdfFrame,
  CfcReportRetryButton,
  CfcReportStatus,
  CfcReportTitle,
} from "./CfcReportPreviewDialog.styles";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { fetchReportPreviewPdf } from "../../utils/reportDownloadRequest";

interface CfcReportPreviewDialogProps {
  open: boolean;
  reportId: string;
  onBack: () => void;
  onClose: () => void;
}

const REPORT_TITLE_ID = "cfc-aneuploidy-report-title";
const REPORT_ERROR_MESSAGE =
  "We couldn't load the aneuploidy test report. Please try again.";
// Hide PDF viewer chrome.
const PDF_VIEWER_OPTIONS =
  "#toolbar=0&navpanes=0&pagemode=none&zoom=page-width";

const createPdfObjectUrl = async (
  token: string,
  reportId: string,
  setLoggedOut: (inactivity: boolean) => void,
  signal: AbortSignal,
): Promise<string> => {
  const response = await fetchReportPreviewPdf(
    token,
    "aneuploidy",
    reportId,
    setLoggedOut,
    { signal },
  );
  if (!response.ok) throw new Error(REPORT_ERROR_MESSAGE);

  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const CfcReportPreviewDialog = ({
  open,
  reportId,
  onBack,
  onClose,
}: CfcReportPreviewDialogProps): React.ReactElement => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const [pdfObjectUrl, setPdfObjectUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    if (!pdfObjectUrl) return undefined;

    return () => {
      URL.revokeObjectURL(pdfObjectUrl);
    };
  }, [pdfObjectUrl]);

  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();

    const loadReport = async () => {
      setPdfObjectUrl("");
      setErrorMessage("");

      if (!auth?.token || !reportId) {
        setIsLoading(false);
        setErrorMessage(REPORT_ERROR_MESSAGE);
        return;
      }

      setIsLoading(true);

      try {
        const objectUrl = await createPdfObjectUrl(
          auth.token,
          reportId,
          setLoggedOut,
          controller.signal,
        );

        if (controller.signal.aborted) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        setPdfObjectUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) {
          setErrorMessage(REPORT_ERROR_MESSAGE);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadReport();

    return () => {
      controller.abort();
    };
  }, [auth?.token, loadAttempt, open, reportId, setLoggedOut]);

  return (
    <CfcReportDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={REPORT_TITLE_ID}
    >
      <CfcReportHeader>
        <CfcReportTitle id={REPORT_TITLE_ID}>
          Aneuploidy test report
        </CfcReportTitle>
        <CfcReportCloseButton
          onClick={onClose}
          aria-label="Close report preview"
        >
          <FontAwesomeIcon icon={faXmark} />
        </CfcReportCloseButton>
      </CfcReportHeader>
      <CfcReportBackBar>
        <CfcReportBackButton type="button" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Back to Unity Confirm test order
        </CfcReportBackButton>
      </CfcReportBackBar>
      <CfcReportFrameArea aria-busy={isLoading}>
        {isLoading && (
          <CfcReportStatus
            role="status"
            aria-label="Loading report"
            aria-live="polite"
          >
            <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
          </CfcReportStatus>
        )}
        {!isLoading && errorMessage && (
          <CfcReportError role="alert">
            <span>{errorMessage}</span>
            <CfcReportRetryButton
              type="button"
              onClick={() => setLoadAttempt((current) => current + 1)}
            >
              Retry
            </CfcReportRetryButton>
          </CfcReportError>
        )}
        {!isLoading && pdfObjectUrl && (
          <CfcReportPdfFrame
            src={`${pdfObjectUrl}${PDF_VIEWER_OPTIONS}`}
            title="Aneuploidy test report"
            referrerPolicy="no-referrer"
          />
        )}
      </CfcReportFrameArea>
    </CfcReportDialog>
  );
};
